// Birlashgan sotuv — «avval balansni to'ldir, keyin paket ber», BITTA
// tasdiq ostida (11-SHERIK-KABINETI-UI.md §10, egasi qarori 3).
//
// =====================================================================
// ⚠️ NEGA `useCashOperation` YETMAYDI
// =====================================================================
// U BITTA amalni bajaradi va natijani bitta `feedback` ga yig'adi. Bu
// yerda esa IKKI MUSTAQIL LEDGER AMALI bor va ular alohida-alohida
// muvaffaqiyatsiz bo'la oladi:
//
//   1) `POST /api/agents/{id}/fund`  — kassir DEPOZITIDAN abonentga
//   2) `POST /api/grants`            — abonent BALANSIDAN paketga
//
// Ya'ni «pul o'tdi, paket berilmadi» — kutilgan, haqiqiy holat. Uni
// bitta natijaga siqish ikki xil yolg'ondan biriga olib kelardi:
// «bajarilmadi» (pul esa allaqachon ko'chgan) yoki «bajarildi» (paket
// esa yo'q). Shuning uchun natija QADAM BO'YICHA saqlanadi.
//
// =====================================================================
// ⚠️ IKKINCHI VA MUHIMROQ SABAB: QAYTA URINISH
// =====================================================================
// Qisman muvaffaqiyatdan keyin kassir «qayta urinish» ni bosadi. Agar
// bu BUTUN oqimni qaytadan yurgizsa, birinchi qadam IKKINCHI marta
// bajarilardi — abonentga ikki barobar pul. Shuning uchun bu yerda ikki
// himoya bor va ular BIR-BIRINI TAKRORLAYDI ataylab:
//
//   · `settledRef` — pul KO'CHGAN qadam boshqa yurgizilmaydi (skip);
//   · idempotentlik kaliti — qadam baribir yuborilsa, server uni
//     TAKROR deb taniydi va pulni ikkinchi marta yechmaydi.
//
// Bittasi yetarli emas: birinchisi faqat shu brauzer sessiyasida
// ishlaydi (sahifa yangilansa yo'qoladi), ikkinchisi esa faqat kalit
// o'zgarmasa ishlaydi.

import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { cashdeskKeys } from './cashdeskKeys'
import type { CashFeedback } from './useCashOperation'

export type SaleStepId = 'fund' | 'grant'

export interface SaleStep {
  id: SaleStepId
  /**
   * Amalning IMZOSI — idempotentlik kaliti aynan shunga bog'lanadi.
   *
   * ⚠️ Imzoga amalning MAZMUNI kiradi, boshqa hech narsa:
   * to'ldirish uchun `(abonent, summa, valyuta)`, paket uchun
   * `(abonent, paket)`. Ikki tomon ham zarur:
   *
   *   · imzo o'zgarmasa kalit ham o'zgarmaydi — tarmoq uzilib qayta
   *     urinilganda server so'rovni TAKROR deb taniydi va pul ikkinchi
   *     marta yechilmaydi;
   *   · imzo o'zgarsa YANGI kalit yasaladi — aks holda kassir paketni
   *     almashtirganda `POST /api/grants` `409 IDEMPOTENCY_CONFLICT`
   *     qaytarardi (band kalit, boshqa amal).
   *
   * ⚠️ Qadamlarning imzosi ALOHIDA: summa o'zgarganda to'ldirish kaliti
   * yangilanadi, paket kaliti esa O'ZGARMAYDI — o'sha paket, o'sha amal.
   */
  signature: string
  /** Jurnalda kalit qaysi amaldan kelganini ko'rsatadi */
  keyPrefix: string
  run: (idempotencyKey: string) => Promise<CashFeedback>
}

export interface CombinedSale {
  /** Qadam natijasi; maydon yo'q — hali urinilmagan */
  outcomes: Readonly<Partial<Record<SaleStepId, CashFeedback>>>
  /** ⚠️ PUL KO'CHGAN qadamlar — qayta yurgizilmaydi */
  settled: ReadonlySet<SaleStepId>
  running: boolean
  /**
   * Berilgan qadamlarni NAVBAT bilan bajaradi va birinchi to'siqda
   * to'xtaydi. Allaqachon o'tgan qadamlar tashlab ketiladi.
   *
   * @returns hamma so'ralgan qadamda pul ko'chdimi
   */
  run: (steps: readonly SaleStep[]) => Promise<boolean>
  /**
   * Sotuv YAKUNLANDI — keyingisi uchun kalitlar yangilanadi.
   *
   * ⚠️ Natija bannerlari QOLADI (§4.4: natija ekranda turadi va uni
   * faqat kassir yopadi), lekin kalitlar tozalanadi. Usiz shu abonentga
   * o'sha summani IKKINCHI marta o'tkazish imkonsiz bo'lardi: imzo bir
   * xil, kalit bir xil, server esa uni takror deb rad etardi va pul
   * hech qachon ko'chmasdi.
   */
  startOver: () => void
  /** Hammasini tozalaydi — bannerlar ham, kalitlar ham */
  reset: () => void
}

const NO_STEPS: ReadonlySet<SaleStepId> = new Set()

/** Pul ko'chgan holatlar. `partial` ham shu yerda — baza yangilangan. */
function moved(feedback: CashFeedback): boolean {
  return feedback.tone === 'success' || feedback.tone === 'partial'
}

export function useCombinedSale(): CombinedSale {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [outcomes, setOutcomes] = useState<Partial<Record<SaleStepId, CashFeedback>>>({})
  const [settled, setSettled] = useState<ReadonlySet<SaleStepId>>(NO_STEPS)
  const [running, setRunning] = useState(false)

  /**
   * ⚠️ HOLAT EMAS, REF — va bu majburiy. `run` sikl ichida `await`
   * qiladi, ya'ni u boshlanganda olingan `settled` NUSXASI bilan
   * ishlardi: birinchi qadam o'tgani ikkinchi aylanishda KO'RINMASDI.
   * Ekran uchun qiymat holatga ko'chiriladi.
   */
  const settledRef = useRef<Set<SaleStepId>>(new Set())

  /** Imzo → kalit. Bir sotuv davomida o'zgarmaydi (`SaleStep.signature`). */
  const keys = useRef(new Map<string, string>())

  const keyFor = useCallback((step: SaleStep): string => {
    const known = keys.current.get(step.signature)
    if (known !== undefined) return known
    const fresh = newIdempotencyKey(step.keyPrefix)
    keys.current.set(step.signature, fresh)
    return fresh
  }, [])

  const run = useCallback(
    async (steps: readonly SaleStep[]): Promise<boolean> => {
      // ⚠️ BIRINCHI HIMOYA: o'tgan qadam ro'yxatdan CHIQARILADI.
      // Chaqiruvchi hamma qadamni bersa ham, pul ikki marta ko'chmaydi.
      const pending = steps.filter((step) => !settledRef.current.has(step.id))
      if (pending.length === 0) return true

      setRunning(true)
      // Faqat HOZIR yuriladigan qadamlarning bannerlari o'chiriladi:
      // «pul o'tdi» xabari qayta urinish davomida ekranda QOLISHI kerak.
      setOutcomes((prev) => {
        const next = { ...prev }
        for (const step of pending) delete next[step.id]
        return next
      })

      let allMoved = true
      try {
        for (const step of pending) {
          let outcome: CashFeedback
          try {
            outcome = await step.run(keyFor(step))
          } catch (error) {
            outcome = { tone: 'error', message: translateError(error, t) }
          }
          setOutcomes((prev) => ({ ...prev, [step.id]: outcome }))

          if (!moved(outcome)) {
            // ⚠️ TO'XTAYDI. Balans to'ldirilmagan bo'lsa paket berish
            // baribir «mablag' yetmaydi» bilan rad etilardi — kassir
            // esa ekranda IKKITA qizil ko'rib, qaysi biri sabab
            // ekanini ajrata olmasdi.
            allMoved = false
            break
          }
          settledRef.current.add(step.id)
          setSettled(new Set(settledRef.current))
        }
      } finally {
        setRunning(false)
        // Prefiks bo'yicha: abonent balansi ham, kassir depoziti ham
        // shu ostida (`useCashOperation` dagi bilan bir xil sabab).
        void queryClient.invalidateQueries({ queryKey: cashdeskKeys.all })
      }
      return allMoved
    },
    [keyFor, queryClient, t],
  )

  const startOver = useCallback(() => {
    settledRef.current = new Set()
    setSettled(NO_STEPS)
    keys.current.clear()
  }, [])

  const reset = useCallback(() => {
    startOver()
    setOutcomes({})
  }, [startOver])

  return { outcomes, settled, running, run, startOver, reset }
}
